import * as React from 'react';
import { useTranslation } from 'react-i18next';
import useFormik from 'hooks/useFormik';
import ActionRow from 'components/ActionRow';
import ButtonWithArrowIcon from 'components/Buttons/ButtonWithArrowIcon';
import { validationSchema } from './validationSchema';

import styles from './styles.module.scss';
import MultipleSelectChipDocuments from '../MultipleSelectChipDocuments';
import { CustomFormControl } from '../../../../components/Form/FormControl';
import { ICandidate } from '../../../../common/request/interfaces/candidate';
import { formatAsPhone } from '../../../../functions/format/formatAsPhone';

type InputProps = {
  onSubmit: (candidate: ICandidate) => void;
  defaultValues?: {
    candidateFirstName?: string;
    candidateLastName?: string;
    candidatePhone?: string;
    candidateEmail?: string;
    candidateEmployeeId?: string;
  };
  disableCandidateFields?: boolean;
};

export const RequestForm = ({
  onSubmit,
  defaultValues,
  disableCandidateFields = false,
}: InputProps) => {
  const { t } = useTranslation();

  const { getFieldProps, getFieldErrorProp, handleSubmit } = useFormik({
    initialValues: {
      candidateFirstName: defaultValues?.candidateFirstName || '',
      candidateLastName: defaultValues?.candidateLastName || '',
      candidatePhone: defaultValues?.candidatePhone || '',
      candidateEmail: defaultValues?.candidateEmail || '',
      candidateEmployeeId: defaultValues?.candidateEmployeeId || '',
      documentGroupName: '',
      documentsNeeded: [],
    },
    validationSchema,
    onSubmit(value: ICandidate) {
      onSubmit({
        ...value,
        candidateFirstName: value.candidateFirstName.trim(),
        candidateLastName: value.candidateLastName.trim(),
        candidateEmail: value.candidateEmail.trim().toLowerCase(),
        candidatePhone: `+1${value.candidatePhone.replace(/\D/g, '')}`.trim(),
      });
    },
  });

  const handlerChangePhoneNumber = (e: any) => {
    getFieldProps('candidatePhone').onChange(
      formatAsPhone(
        e.target.value.toString(),
        getFieldProps('candidatePhone').value
      )
    );
  };

  return (
    <form onSubmit={handleSubmit} className={styles.form}>
      <div className={styles.row}>
        <CustomFormControl
          error={getFieldErrorProp('candidateFirstName').error}
          label={t('RequestForm.label.candidateFirstName')}
          required={true}
          inputProps={getFieldProps('candidateFirstName')}
          disabled={disableCandidateFields}
        />
        <CustomFormControl
          error={getFieldErrorProp('candidateLastName').error}
          label={t('RequestForm.label.candidateLastName')}
          required={true}
          inputProps={getFieldProps('candidateLastName')}
          disabled={disableCandidateFields}
        />
      </div>
      <div className={styles.row}>
        <CustomFormControl
          error={getFieldErrorProp('candidatePhone').error}
          label={t('RequestForm.label.candidatePhone')}
          required={true}
          inputProps={{
            ...getFieldProps('candidatePhone'),
            onChange: handlerChangePhoneNumber,
          }}
          disabled={disableCandidateFields}
        />
        <CustomFormControl
          error={getFieldErrorProp('candidateEmail').error}
          label={t('RequestForm.label.candidateEmail')}
          required={false}
          inputProps={getFieldProps('candidateEmail')}
          disabled={disableCandidateFields}
        />
      </div>
      <div className={styles.row}>
        <CustomFormControl
          error={getFieldErrorProp('candidateEmployeeId').error}
          label={t('RequestForm.label.candidateEmployeeId')}
          required={false}
          inputProps={getFieldProps('candidateEmployeeId')}
          disabled={disableCandidateFields}
        />
        <MultipleSelectChipDocuments
          error={!!getFieldErrorProp('documentsNeeded').error}
          label="Documents Needed"
          onChange={getFieldProps('documentsNeeded').onChange}
          selected={getFieldProps('documentsNeeded').value}
        />
      </div>
      <ActionRow style="right" marginTop={false} className={styles.actions}>
        <ButtonWithArrowIcon type="submit">
          {t('RequestForm.btn.submit')}
        </ButtonWithArrowIcon>
      </ActionRow>
    </form>
  );
};
